/**
 * Ranking weights live in the query string, so a tuned view can be bookmarked
 * and returned to with the back button.
 *
 * The values are the engine's own tunables rather than normalised 0-100
 * "importance" scores, so what the slider says is what the engine runs with.
 * Key order is fixed because these objects go into query keys, which are hashed
 * positionally: the same weights built in a different order must not become a
 * different cache entry.
 */

export interface Tuning {
  w_rating: number
  w_effort: number
  w_explore: number
  w_penalty: number
}

export type TuningKey = keyof Tuning

export const DEFAULT_TUNING: Tuning = {
  w_rating: 1.0,
  w_effort: 0.25,
  w_explore: 0.5,
  w_penalty: 1.0,
}

export interface TuningField {
  key: TuningKey
  label: string
  hint: string
  min: number
  max: number
  step: number
  format?: (value: number) => string
}

export const TUNING_FIELDS: TuningField[] = [
  {
    key: 'w_rating',
    label: 'Rating',
    hint: 'How much a recipe’s rating counts for.',
    min: 0,
    max: 2,
    step: 0.05,
  },
  {
    key: 'w_effort',
    label: 'Effort penalty',
    hint: 'How much a laborious recipe costs you.',
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'w_explore',
    label: 'Explore new things',
    hint: 'The nudge towards recipes you have not made in a while.',
    min: 0,
    max: 2,
    step: 0.05,
  },
  {
    key: 'w_penalty',
    label: 'Combined effort penalty',
    hint: 'Extra penalty when both meals are a lot of work.',
    min: 0,
    max: 3,
    step: 0.05,
    format: (value) => `${value.toFixed(2)}x`,
  },
]

function fieldFor(key: TuningKey): TuningField {
  const field = TUNING_FIELDS.find((entry) => entry.key === key)
  if (!field) throw new Error(`unknown tuning key: ${key}`)
  return field
}

/** Falls back to the default for missing, unparseable or out-of-range values. */
export function parseTuning(params: URLSearchParams): Tuning {
  const tuning = {} as Tuning
  for (const key of Object.keys(DEFAULT_TUNING) as TuningKey[]) {
    const field = fieldFor(key)
    const raw = Number(params.get(key))
    const value = params.get(key) === null ? Number.NaN : raw
    const usable =
      Number.isFinite(value) && value >= field.min && value <= field.max ? value : null
    tuning[key] = usable ?? DEFAULT_TUNING[key]
  }
  return tuning
}

/** Whether the URL names any weight at all, and so outranks remembered ones. */
export function hasTuningParam(params: URLSearchParams): boolean {
  return (Object.keys(DEFAULT_TUNING) as TuningKey[]).some((key) => params.has(key))
}

/** Adopts stored weights, falling back per value when one is unusable. */
export function tuningFromWeights(
  weights: Partial<Record<TuningKey, number>> | null | undefined,
): Tuning {
  const tuning = { ...DEFAULT_TUNING }
  if (!weights) return tuning
  for (const key of Object.keys(DEFAULT_TUNING) as TuningKey[]) {
    const field = fieldFor(key)
    const value = weights[key]
    if (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= field.min &&
      value <= field.max
    ) {
      tuning[key] = value
    }
  }
  return tuning
}

/** By value, so callers can compare freshly parsed objects. */
export function sameTuning(a: Tuning, b: Tuning): boolean {
  return (Object.keys(DEFAULT_TUNING) as TuningKey[]).every(
    (key) => a[key] === b[key],
  )
}

export function activeTuningCount(tuning: Tuning): number {
  return (Object.keys(DEFAULT_TUNING) as TuningKey[]).filter(
    (key) => tuning[key] !== DEFAULT_TUNING[key],
  ).length
}
